import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LoyaltyPointsSliderSearch from 'loyalty/components/LoyaltyPointsSliderSearch'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import getIsExperienceLoyaltyBurnEnabled from 'loyalty/selectors/getIsExperienceLoyaltyBurnEnabled'
import { useAppSelector } from 'hooks/reduxHooks'
import { getExperienceSearchBurnCalculationKey } from 'lib/experiences/getExperienceSearchBurnCalculationKey'
import usePrefetchExperienceSearchBurnCalculations from '../hooks/usePrefetchExperienceSearchBurnCalculations'

const SliderContainer = styled.div`
  margin-bottom: ${rem(16)};
  min-width: 0;
`

const ListContainer = styled.div`
  margin-top: ${rem(4)};
`

interface Props {
  listIds?: Array<string>
}

function ExperienceSearchSocieteFilters({ listIds }: Props) {
  const intl = useIntl()
  const isLoyaltyEnabled = useAppSelector(getIsExperienceLoyaltyBurnEnabled)
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  usePrefetchExperienceSearchBurnCalculations(listIds)
  const { setSearchBurnEnabled, setPointsToBurn } = useSearchBurnEnabled()

  const viewPriceInPoints = !!searchFilters.viewPriceInPoints
  const experienceSearchKey = useMemo(
    () => getExperienceSearchBurnCalculationKey(searchFilters),
    [searchFilters],
  )

  const clearViewPriceInPoints = useCallback(() => {
    setSearchFilters({ ...searchFilters, viewPriceInPoints: undefined })
  }, [searchFilters, setSearchFilters])

  const handleViewPriceInPointsToggle = useCallback(() => {
    if (viewPriceInPoints) {
      clearViewPriceInPoints()
    } else {
      setSearchBurnEnabled(false)
      setPointsToBurn(undefined)
      setSearchFilters({ ...searchFilters, viewPriceInPoints: true })
    }
  }, [viewPriceInPoints, clearViewPriceInPoints, searchFilters, setSearchFilters, setSearchBurnEnabled, setPointsToBurn])

  if (!isLoyaltyEnabled) {
    return null
  }

  const viewPriceInPointsLabel = intl.formatMessage({
    id: 'Pl7ew0Wylbx4ZUv7DdgF',
    defaultMessage: 'View Price in Points',
    description: 'Checkbox label to show experience search prices as Société points only',
  })

  return (
    <FilterPanelFilterGroup
      title={<LoyaltyTierLabel kind="primary">{getLoyaltyProgramDisplayName()}</LoyaltyTierLabel>}
    >
      <SliderContainer>
        <LoyaltyPointsSliderSearch
          layout="filterPanel"
          cacheKey={experienceSearchKey}
          skipBatchMaxCap
          skipBurnExperiment
          onBurnEnabled={clearViewPriceInPoints}
          disabled={viewPriceInPoints}
        />
      </SliderContainer>
      <ListContainer>
        <CheckboxInputListItem
          inputPlacement="start"
          checked={viewPriceInPoints}
          onChange={handleViewPriceInPointsToggle}
          title={viewPriceInPointsLabel}
          size="medium"
        />
      </ListContainer>
    </FilterPanelFilterGroup>
  )
}

export default ExperienceSearchSocieteFilters
